import L from 'leaflet';
import { feature, merge, neighbors } from 'topojson-client';
import {
    AREA_TOGGLE_FADE_MS,
    anyListingsShown,
    areaGrowAt,
    areaToggleFor,
    beginRevealColour,
    easeAreaFade,
    endRevealColour,
    noteAreaReveal,
    noteSelectionHold,
    setAreaGrowSchedule,
    setAreaShrinkSchedule,
    setAreasRevealPending,
    whenAreaFadeReady,
} from './areaToggleFade.js';

// After the last area lights up and its homes grow in, the reveal is done: settle the tiles to the Färger
// toggle this long later (the homes' grow/presence animation).
const REVEAL_COLOUR_SETTLE_MS = 850;

// The selected areas of a search, as both views show them: everything outside the selection is dimmed
// (its colours still show through) and one green line runs around the whole selection (the areas merged
// with topojson's merge, so inner borders don't get the line). Drawn in its own pane above the areas and
// below the place labels; it ignores the mouse, so hovering and clicking areas works through it.
//
// Changes fade (areaToggleFade.js):
// - An area clicked into or out of the selection: its dimming is a patch of its own that fades out, or
//   (leaving, once its homes have gone) fades in before the overlay is redrawn.
// - Areas a search adds stay dimmed, their homes off the map, until the map has stopped flying there; then
//   their dimming lifts one area after another, spreading from the areas already selected through
//   neighbouring areas, and each area's homes grow in just after it has lit up. Areas a search drops go the
//   same way round: each area's homes shrink in that order, and each area is dimmed just after its homes.
// - The first selection fades the dimming in as a whole once the map has stopped; clearing it fades it out.

const PANE = 'areaSelectionPane';
const DIM_FILL = '#2c3634';
const DIM_OPACITY = 0.5;
const LINE_COLOR = '#1f7a62';
// Reveal veil: each area joining the selection starts dimmed exactly like an unselected area (the same dark
// cover), then the veil is lifted one after another (revealOrder) so the metric colour fades in as the reveal
// front reaches it. Its own pane (own opacity, always 1) so it doesn't ride the dimming pane's fade. Above the
// area colours (overlay ~400), below the labels (440).
const VEIL_PANE = 'areaRevealVeilPane';
const VEIL_FILL = DIM_FILL;
const VEIL_OPACITY = DIM_OPACITY;
// Above this many areas (a whole län), skip the per-area veils for a first selection to keep it light; the
// colour then appears as before. A kommun (~100–300 areas) is well under it.
const VEIL_MAX_RIPPLE = 800;
// Wide enough to cover any view of Sweden.
const WORLD_RING = [[-85, -180], [-85, 180], [85, 180], [85, -180]];
// The areas of one change light up over at most this long (the last one starts then).
const STAGGER_MAX_MS = 900;
const STAGGER_STEP_MS = 45;
// After the map stops (or the change, when it doesn't move), in case a fly to the selection is about to start.
const SETTLE_MS = 150;
// Areas never wait longer than this for the map.
const IDLE_FALLBACK_MS = 2500;
// How long an announced fly (expectMove) may take to start before the reveal stops waiting for it.
const EXPECT_MOVE_MAX_MS = 1200;
// Once the map has stopped, wait up to this long for the base map to report it has finished rendering
// (openFreeMap fires 'baseidle'); a fallback for the raster fallback map, which never reports it.
const BASE_IDLE_WAIT_MS = 1200;

const toLatLngRing = (ring) => ring.map(([lng, lat]) => [lat, lng]);
const codeOf = (geometry) => String(geometry?.properties?.regsokod || '').trim();

// Per topology: each area's index and its neighbours (areas sharing a border).
const adjacencyCache = new WeakMap();
const adjacencyFor = (topology) => {
    let adjacency = adjacencyCache.get(topology);
    if (!adjacency) {
        const object = topology.objects[Object.keys(topology.objects)[0]];
        const geometries = object?.geometries || [];
        const indexByCode = new Map(geometries.map((geometry, index) => [codeOf(geometry), index]));
        adjacency = { geometries, indexByCode, neighbours: neighbors(geometries) };
        adjacencyCache.set(topology, adjacency);
    }
    return adjacency;
};

const centreOf = (topology, geometries) => {
    if (!geometries.length) return null;
    const bounds = L.geoJSON(feature(topology, { type: 'GeometryCollection', geometries })).getBounds();
    return bounds.isValid() ? bounds.getCenter() : null;
};

// The areas in the order they light up: first those bordering the anchor (the areas already selected), then
// outwards through neighbours, so connected areas follow each other. A part that touches neither starts
// from its area nearest the anchor.
const revealOrder = (topology, codes, anchorCodes) => {
    const { geometries, indexByCode, neighbours } = adjacencyFor(topology);
    const pending = new Set(codes.filter((code) => indexByCode.has(code)));
    const anchor = new Set([...anchorCodes].filter((code) => indexByCode.has(code)));
    const order = [];
    const visit = (seeds) => {
        const queue = [...seeds];
        seeds.forEach((code) => pending.delete(code));
        while (queue.length) {
            const code = queue.shift();
            order.push(code);
            neighbours[indexByCode.get(code)].forEach((index) => {
                const next = codeOf(geometries[index]);
                if (!pending.has(next)) return;
                pending.delete(next);
                queue.push(next);
            });
        }
    };
    const touchingAnchor = [...pending].filter((code) => (
        neighbours[indexByCode.get(code)].some((index) => anchor.has(codeOf(geometries[index])))
    ));
    if (touchingAnchor.length) visit(touchingAnchor);
    if (pending.size) {
        const anchorCentre = centreOf(topology, [...anchor].map((code) => geometries[indexByCode.get(code)]))
            || centreOf(topology, [...pending].map((code) => geometries[indexByCode.get(code)]));
        const centres = new Map([...pending].map((code) => [code, centreOf(topology, [geometries[indexByCode.get(code)]])]));
        const distance = (code) => (centres.get(code) && anchorCentre ? centres.get(code).distanceTo(anchorCentre) : Infinity);
        while (pending.size) {
            const nearest = [...pending].reduce((best, code) => (distance(code) < distance(best) ? code : best));
            visit([nearest]);
        }
    }
    return order;
};

const staggerDelays = (count) => {
    const total = Math.min(STAGGER_MAX_MS, Math.max(0, count - 1) * STAGGER_STEP_MS);
    return Array.from({ length: count }, (_, index) => (count > 1 ? (index / (count - 1)) * total : 0));
};

export const createAreaSelectionOverlay = (map) => {
    const pane = map.getPane(PANE) || map.createPane(PANE);
    pane.style.zIndex = '420';
    pane.style.pointerEvents = 'none';
    const renderer = L.canvas({ pane: PANE, padding: 0.5 });
    const veilPane = map.getPane(VEIL_PANE) || map.createPane(VEIL_PANE);
    veilPane.style.zIndex = '421';
    veilPane.style.pointerEvents = 'none';
    const veilRenderer = L.canvas({ pane: VEIL_PANE, padding: 0.5 });
    let veilGroup = null;
    let veilRafId = null;
    let veilPairs = [];
    let group = null;
    let lastKey = '';
    let lastIds = new Set();
    let fadeRafId = null;
    // The hovered area is cut out of the dimming so its coloured map shows (like an active tile): the world
    // dim polygon and its base holes are kept, and the hovered area's rings are added as extra holes.
    let lastTopology = null;
    let worldDim = null;
    let baseHoles = [];
    let hoverCode = null;
    // Bumped on every change, so waits and fades from an earlier one let go.
    let generation = 0;
    // Areas waiting to light up (homes kept off the map), released when a change replaces them.
    let waitingCodes = [];
    // Pending "settle the revealed tiles to the Färger toggle" timer (reveal-colour, areaToggleFade.js).
    let revealColourTimer = null;
    const scheduleRevealColourEnd = (gen, delayMs) => {
        if (revealColourTimer) clearTimeout(revealColourTimer);
        revealColourTimer = setTimeout(() => {
            revealColourTimer = null;
            if (gen === generation) endRevealColour();
        }, Math.max(0, delayMs));
    };

    // Whether the map is moving (a fly to the selection), and what waits for it to stop.
    let moving = false;
    let idleWaiters = [];
    // Set by expectMove() until the announced fly has started: a moveend before that (on a phone, Leaflet's
    // resize moveend after the keyboard closes) isn't the fly ending, so it doesn't release the reveal.
    let expectingMove = false;
    let expectTimer = null;
    const flushIdle = () => {
        const waiters = idleWaiters;
        idleWaiters = [];
        waiters.forEach((waiter) => waiter());
    };
    const handleMoveStart = () => {
        moving = true;
        expectingMove = false;
        clearTimeout(expectTimer);
    };
    const handleMoveEnd = () => {
        if (expectingMove) return;
        moving = false;
        flushIdle();
    };
    map.on('movestart', handleMoveStart);
    map.on('moveend', handleMoveEnd);
    // Runs callback once the map has stopped AND the base map has finished rendering (so the homes don't
    // appear on a still-rendering, blurry map and then glide into place as it sharpens); at most
    // IDLE_FALLBACK_MS from now.
    const whenIdle = (callback) => {
        let done = false;
        let baseTimer = null;
        const run = () => {
            if (done) return;
            done = true;
            map.off('baseidle', run);
            if (baseTimer) clearTimeout(baseTimer);
            callback();
        };
        // Wait for the base map to report it has finished rendering; if it already has, go now.
        const waitForBase = () => {
            if (done) return;
            if (map.__ofmBaseIdle) { run(); return; }
            map.once('baseidle', run);
            baseTimer = setTimeout(run, BASE_IDLE_WAIT_MS);
        };
        const check = () => {
            if (moving) idleWaiters.push(waitForBase);
            else waitForBase();
        };
        setTimeout(check, SETTLE_MS);
        setTimeout(run, IDLE_FALLBACK_MS);
    };

    const stopFade = () => {
        if (fadeRafId) cancelAnimationFrame(fadeRafId);
        fadeRafId = null;
    };

    const clear = () => {
        stopFade();
        clearVeils();
        if (group) {
            group.remove();
            group = null;
        }
        worldDim = null;
        baseHoles = [];
    };

    const releaseWaiting = () => {
        if (!waitingCodes.length) return;
        const codes = waitingCodes;
        waitingCodes = [];
        setAreasRevealPending(codes, false);
    };

    const geometriesFor = (topology, ids) => {
        const object = topology.objects[Object.keys(topology.objects)[0]];
        return (object?.geometries || []).filter((geometry) => ids.has(codeOf(geometry)));
    };

    const draw = (topology, ids, patchIds = []) => {
        clear();
        pane.style.opacity = '';
        lastTopology = topology;
        const geometries = geometriesFor(topology, ids);
        if (!geometries.length) return [];
        const merged = merge(topology, geometries);
        const polygons = merged.type === 'Polygon' ? [merged.coordinates] : merged.coordinates;
        // Outside the selection: the world with every ring of the selection cut out, filled even-odd. The
        // selection's holes (an unselected area inside it, e.g. a town inside its picked countryside) are
        // dimmed by the same parity, and so is anything picked inside such a hole lit again. One dim polygon
        // for all of it, so the hover cut-out (applyHoverHole) works the same for an area inside a hole.
        const holes = polygons.flatMap((polygon) => polygon.map(toLatLngRing));
        // A fading area's own dimming, over its spot in the selection.
        const patches = patchIds.map((id) => {
            const patch = merge(topology, geometriesFor(topology, new Set([id])));
            const rings = (patch.type === 'Polygon' ? [patch.coordinates] : patch.coordinates)
                .map((polygon) => polygon.map(toLatLngRing));
            return L.polygon(rings, {
                renderer, interactive: false, stroke: false, fillColor: DIM_FILL, fillOpacity: 0,
            });
        });
        baseHoles = holes;
        worldDim = L.polygon([WORLD_RING, ...holes], {
            renderer, interactive: false, stroke: false, fillColor: DIM_FILL, fillOpacity: DIM_OPACITY,
        });
        group = L.layerGroup([
            worldDim,
            ...patches,
            // The line runs along every edge of the selection, around an unselected area inside it too. (Areas
            // clicked off inside a search are still passed in as part of it, so they never make a hole.)
            L.polyline(holes, {
                renderer, interactive: false, color: LINE_COLOR, weight: 3, opacity: 0.95, lineJoin: 'round',
            }),
        ]).addTo(map);
        applyHoverHole();
        return patches;
    };

    // Cut the hovered area out of the world dim so its coloured map shows through, like an active tile.
    const hoverRingsFor = (code) => {
        // Every area outside the selection is dimmed all over (one even-odd polygon, see draw), so adding all
        // of its rings flips exactly its own ground: its outer edge lights it, and its holes (an area inside
        // it, picked or not) flip back and keep their look. Only its outer ring would also flip what lies in
        // its holes: a picked town inside it would go dark. One already in the selection is lit already, and
        // cutting it again would re-dim it.
        if (!code || !lastTopology || lastIds.has(code)) return [];
        const geoms = geometriesFor(lastTopology, new Set([code]));
        if (!geoms.length) return [];
        const merged = merge(lastTopology, geoms);
        const polygons = merged.type === 'Polygon' ? [merged.coordinates] : merged.coordinates;
        return polygons.flatMap((polygon) => polygon.map(toLatLngRing));
    };
    const applyHoverHole = () => {
        if (!worldDim) return;
        const rings = hoverRingsFor(hoverCode);
        worldDim.setLatLngs([WORLD_RING, ...baseHoles, ...rings]);
    };

    // Fades patches' dimming in (dimming) or out, each starting after its own delay, then calls onDone.
    const fadePatches = (patches, dimming, onDone, delays = []) => {
        stopFade();
        const startedAt = performance.now();
        const last = Math.max(0, ...delays);
        const step = (now) => {
            const elapsed = now - startedAt;
            patches.forEach((patch, index) => {
                const t = Math.min(1, Math.max(0, (elapsed - (delays[index] || 0)) / AREA_TOGGLE_FADE_MS));
                const eased = easeAreaFade(t);
                patch.setStyle({ fillOpacity: DIM_OPACITY * (dimming ? eased : 1 - eased) });
            });
            if (elapsed < last + AREA_TOGGLE_FADE_MS) fadeRafId = requestAnimationFrame(step);
            else {
                fadeRafId = null;
                onDone();
            }
        };
        fadeRafId = requestAnimationFrame(step);
    };

    // The whole overlay (the pane) from one opacity to another.
    const fadePane = (from, to, onDone) => {
        stopFade();
        pane.style.opacity = String(from);
        const startedAt = performance.now();
        const step = (now) => {
            const t = Math.min(1, (now - startedAt) / AREA_TOGGLE_FADE_MS);
            pane.style.opacity = String(from + ((to - from) * easeAreaFade(t)));
            if (t < 1) fadeRafId = requestAnimationFrame(step);
            else {
                fadeRafId = null;
                // Fading in ends at the default opacity. Fading out holds the pane invisible: onDone (clear())
                // removes the dim layers, but their shared canvas only re-clears on the next frame, so resetting
                // to the default (opacity 1) here would flash the still-drawn dim at full strength for one frame.
                pane.style.opacity = to <= 0 ? String(to) : '';
                onDone?.();
            }
        };
        fadeRafId = requestAnimationFrame(step);
    };

    // The reveal veils (one per area joining the selection): a light cover that each area wears until its homes
    // grow in, lifted in step with them. The lift is driven by the shared grow schedule (areaGrowAt), not by a
    // timer of its own, so it stays in sync with the homes however long the fly to the selection takes.
    const clearVeils = () => {
        if (veilRafId) { cancelAnimationFrame(veilRafId); veilRafId = null; }
        if (veilGroup) { veilGroup.remove(); veilGroup = null; }
        veilPairs = [];
    };
    const driveVeils = (gen) => {
        const startedAt = performance.now();
        const step = () => {
            veilRafId = null;
            if (gen !== generation || !veilPairs.length) return;
            const now = performance.now();
            let anyVisible = false;
            let anyScheduled = false;
            veilPairs.forEach((pair) => {
                const growAt = areaGrowAt(pair.code);
                let opacity = VEIL_OPACITY;
                if (growAt != null) {
                    anyScheduled = true;
                    // Lift ending just as the homes grow (areaGrowAt), so the colour is in by then.
                    const t = Math.min(1, Math.max(0, (now - (growAt - (AREA_TOGGLE_FADE_MS * 0.8))) / AREA_TOGGLE_FADE_MS));
                    opacity = VEIL_OPACITY * (1 - easeAreaFade(t));
                }
                if (opacity > 0.004) anyVisible = true;
                if (Math.abs(opacity - pair.last) > 0.01) {
                    pair.last = opacity;
                    pair.veil.setStyle({ fillOpacity: opacity });
                }
            });
            // Done once the schedule has been read and every veil has lifted (or a safety timeout).
            if ((anyScheduled && !anyVisible) || now - startedAt > 6000) { clearVeils(); return; }
            veilRafId = requestAnimationFrame(step);
        };
        veilRafId = requestAnimationFrame(step);
    };
    const makeVeils = (topology, orderedCodes) => {
        clearVeils();
        const veils = orderedCodes.map((code) => {
            const merged = merge(topology, geometriesFor(topology, new Set([code])));
            const rings = (merged.type === 'Polygon' ? [merged.coordinates] : merged.coordinates)
                .map((polygon) => polygon.map(toLatLngRing));
            return L.polygon(rings, {
                renderer: veilRenderer, interactive: false, stroke: false, fillColor: VEIL_FILL, fillOpacity: VEIL_OPACITY,
            });
        });
        veilGroup = L.layerGroup(veils).addTo(map);
        veilPairs = orderedCodes.map((code, index) => ({ veil: veils[index], code, last: VEIL_OPACITY }));
        driveVeils(generation);
    };

    // Areas just clicked in or out of a selection that stays (areaToggleFade.js): those fade on their own.
    const updateClicked = (topology, ids, added, removed, drawKey) => {
        // Removed ones keep their place in the selection until their dimming has faded in.
        const patches = draw(topology, new Set([...ids, ...removed]), [...added, ...removed]);
        const addedPatches = patches.slice(0, added.length);
        const removedPatches = patches.slice(added.length);
        addedPatches.forEach((patch) => patch.setStyle({ fillOpacity: DIM_OPACITY }));
        if (addedPatches.length) {
            fadePatches(addedPatches, false, () => {
                if (!removedPatches.length) addedPatches.forEach((patch) => patch.remove());
            });
        }
        if (removedPatches.length) {
            let waiting = removed.length;
            removed.forEach((id) => whenAreaFadeReady(id, () => {
                waiting -= 1;
                if (waiting > 0 || lastKey !== drawKey) return;
                fadePatches(removedPatches, true, () => {
                    if (lastKey === drawKey) draw(topology, ids);
                });
            }));
        }
    };

    // A search's change: added areas light up one after another once the map has stopped, removed ones get
    // dimmed one after another once their homes have gone.
    const updateSearched = (topology, ids, previousIds, added, removed, gen) => {
        const removedSet = new Set(removed);
        const addedOrder = revealOrder(topology, added, [...previousIds].filter((id) => !removedSet.has(id)));
        const removedOrder = revealOrder(topology, removed, [...ids]);
        // Removed areas get grey dim patches (dimming in); added areas get light veils, lifted in order so
        // their metric colour fades in.
        const removedPatches = draw(topology, new Set([...ids, ...removed]), removedOrder);
        // The added areas' veils lift on their own (driveVeils) as the grow schedule set below is read.
        if (addedOrder.length) makeVeils(topology, addedOrder);
        const removedDelays = staggerDelays(removedPatches.length);
        // Each dropped area's homes shrink in turn (listingGroupAnimation.js), and its dimming follows them.
        if (removedPatches.length) setAreaShrinkSchedule(removedOrder, removedDelays);
        const fadeIn = () => {
            if (gen !== generation) return;
            const delays = staggerDelays(addedOrder.length);
            noteAreaReveal(delays[delays.length - 1] || 0);
            // Each new area's homes grow in once its own veil is nearly lifted.
            const now = performance.now();
            setAreaGrowSchedule(addedOrder, delays.map((delay) => now + delay + (AREA_TOGGLE_FADE_MS * 0.8)));
            releaseWaiting();
            // Once the last area has lit up and its homes have grown in, settle the tiles to the Färger toggle.
            scheduleRevealColourEnd(gen, (delays[delays.length - 1] || 0) + AREA_TOGGLE_FADE_MS + REVEAL_COLOUR_SETTLE_MS);
        };
        const fadeOut = () => {
            if (gen !== generation) return;
            // When the removed areas have dimmed in, redraw without their place in the selection or the patches.
            fadePatches(removedPatches, true, () => { if (gen === generation) draw(topology, ids); }, removedDelays);
        };
        if (addedOrder.length) {
            waitingCodes = addedOrder;
            setAreasRevealPending(addedOrder, true);
            beginRevealColour(addedOrder);
        }
        // Both at once: dim the removed ones first, then light the new ones.
        if (removedPatches.length) {
            noteSelectionHold({ hold: anyListingsShown(removed) }, () => {
                fadeOut();
                if (addedOrder.length) whenIdle(() => setTimeout(fadeIn, AREA_TOGGLE_FADE_MS));
            });
        } else if (addedOrder.length) {
            whenIdle(fadeIn);
        }
    };

    return {
        // A fly to the selection is about to start (kartvy starts it a couple of frames after a pick, once the
        // pick's re-render has run): count the map as moving already, so the reveal waits for the fly's end
        // instead of running at the old view. The fly's own moveend clears it (IDLE_FALLBACK_MS caps the wait).
        expectMove() {
            moving = true;
            expectingMove = true;
            clearTimeout(expectTimer);
            // If the fly never starts, don't hold the reveal for long.
            expectTimer = setTimeout(() => {
                if (!expectingMove) return;
                expectingMove = false;
                moving = false;
                flushIdle();
            }, EXPECT_MOVE_MAX_MS);
        },
        // The hovered area (its code), cut out of the dimming so its coloured map shows; null clears it.
        // Safe to call for any area: one already outside the dim (selected) is unaffected.
        setHoverHole(regsoCode) {
            const next = regsoCode || null;
            if (next === hoverCode) return;
            hoverCode = next;
            applyHoverHole();
        },
        // regsoIds: the selected areas (an empty set clears it).
        update(topology, regsoIds) {
            const ids = regsoIds instanceof Set ? regsoIds : new Set(regsoIds || []);
            const key = topology && ids.size ? `${Object.keys(topology.objects || {})[0]}:${[...ids].sort().join(',')}` : '';
            if (key === lastKey) return;
            const previousIds = lastIds;
            lastKey = key;
            lastIds = new Set(key ? ids : []);
            generation += 1;
            const gen = generation;
            releaseWaiting();
            // Any reveal still showing full colour settles now, before this change starts its own reveal.
            if (revealColourTimer) { clearTimeout(revealColourTimer); revealColourTimer = null; }
            endRevealColour();
            if (!key) {
                // Cleared: the dimming fades out once the homes have gone.
                if (!group || !topology || !previousIds.size) {
                    clear();
                    return;
                }
                stopFade();
                noteSelectionHold({ hold: anyListingsShown([...previousIds]) }, () => {
                    if (gen !== generation) return;
                    fadePane(1, 0, () => {
                        if (gen === generation) clear();
                    });
                });
                return;
            }
            const added = [...ids].filter((id) => !previousIds.has(id));
            const removed = [...previousIds].filter((id) => !ids.has(id));
            if (!added.length && !removed.length) {
                draw(topology, ids);
                return;
            }
            const clicked = previousIds.size > 0 && added.length + removed.length <= 4
                && [...added, ...removed].every((id) => areaToggleFor(id));
            if (clicked) {
                updateClicked(topology, ids, added, removed, key);
                return;
            }
            if (!previousIds.size) {
                // The first selection: the dimming around it fades in once the map has stopped, and the
                // selected areas' colour fades in one after another (a veil lifted in revealOrder) with their
                // homes.
                draw(topology, ids);
                const order = revealOrder(topology, added, []);
                // The veils lift on their own (driveVeils) as the grow schedule set below is read.
                if (order.length <= VEIL_MAX_RIPPLE) makeVeils(topology, order);
                pane.style.opacity = '0';
                waitingCodes = order;
                setAreasRevealPending(order, true);
                beginRevealColour(order);
                whenIdle(() => {
                    if (gen !== generation) return;
                    const delays = staggerDelays(order.length);
                    noteAreaReveal(delays[delays.length - 1] || 0);
                    const now = performance.now();
                    setAreaGrowSchedule(order, delays.map((delay) => now + delay + (AREA_TOGGLE_FADE_MS * 0.8)));
                    releaseWaiting();
                    fadePane(0, 1);
                    scheduleRevealColourEnd(gen, (delays[delays.length - 1] || 0) + AREA_TOGGLE_FADE_MS + REVEAL_COLOUR_SETTLE_MS);
                });
                return;
            }
            updateSearched(topology, ids, previousIds, added, removed, gen);
        },
        remove() {
            generation += 1;
            releaseWaiting();
            clear();
            pane.style.opacity = '';
            lastKey = '';
            lastIds = new Set();
            map.off('movestart', handleMoveStart);
            map.off('moveend', handleMoveEnd);
            clearTimeout(expectTimer);
            idleWaiters = [];
        },
    };
};
